"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.replacePropertiesInExpressionTokens = exports.replacePropertiesInExpression = exports.spliceTokens = exports.serializeTokens = exports.walkTokens = exports.isPropToken = exports.replaceVariableInExpression = exports.replacePropertyInExpression = void 0;
const representation_1 = require("./representation");
const error_1 = require("./error");
const __1 = require("..");
const lodash_1 = require("lodash");
const parser_1 = require("../language/parser");
const replacePropertyInExpression = (expression, prev, next) => replaceFunctionArgument(expression, 'prop', prev, next);
exports.replacePropertyInExpression = replacePropertyInExpression;
const replaceVariableInExpression = (expression, prev, next) => replaceFunctionArgument(expression, 'variable', prev, next);
exports.replaceVariableInExpression = replaceVariableInExpression;
function replaceFunctionArgument(expression, nodeName, prev, next) {
    const replaceArgument = (node) => {
        if ((0, representation_1.isFunctionNode)(node)) {
            if (node.name === nodeName) {
                const variableName = node.children[0];
                if ((0, representation_1.isLiteralNode)(variableName) && variableName.value === prev) {
                    const token = variableName.expression.revert();
                    // Note that token positions go out of sync after this operation. Should update them in the chain.
                    token.lexeme = `"${next}"`;
                    return { ...node, children: [{ ...variableName, value: next }] };
                }
            }
            return { ...node, children: node.children.map(replaceArgument) };
        }
        return node;
    };
    const [node, ...rest] = (0, representation_1.parseSource)(expression);
    if (rest.length > 0) {
        throw new error_1.InputError('Unexpected expression', rest[0]);
    }
    return nodeToSource(replaceArgument(node));
}
const nodeToSource = (node) => node.expression.revert().toSource();
const getArgumentsTokens = (tokens, fromIndex) => {
    let depth = 0;
    for (let i = fromIndex; i < tokens.length; i++) {
        if (tokens[i].type === __1.TokenType.LeftParen) {
            depth++;
        }
        else if (tokens[i].type === __1.TokenType.RightParen) {
            depth--;
            if (depth === 0) {
                return tokens.slice(fromIndex, i + 1);
            }
        }
    }
    return [];
};
const isPropToken = (token) => token.type === __1.TokenType.Identifier && token.lexeme === 'prop';
exports.isPropToken = isPropToken;
/**
 * For each token execute the callback passing in the token, and if the token is followed by a complete
 * argument list, the tokens of the argument list, including the parenthesis. The tokens are replaced by
 * the tokens returned by the callback.
 */
const walkTokens = (tokens, cb) => {
    tokens = [...tokens];
    for (let i = 0; i < tokens.length; i++) {
        const isFunctionCall = tokens[i].type === __1.TokenType.Identifier && (0, lodash_1.nth)(tokens, i + 1)?.type === __1.TokenType.LeftParen;
        const children = isFunctionCall ? getArgumentsTokens(tokens, i + 1) : [];
        (0, exports.spliceTokens)(tokens, i, children.length + 1, ...cb(tokens[i], children));
    }
    return tokens;
};
exports.walkTokens = walkTokens;
const serializeTokens = (tokens) => tokens.map((token) => token.lexeme).join('');
exports.serializeTokens = serializeTokens;
const spliceTokens = (tokens, position, deleteCount, ...newTokens) => {
    const line = (0, lodash_1.nth)(tokens, position)?.line ?? 1;
    const firstStart = (0, lodash_1.nth)(tokens, position)?.start ?? 0;
    const afterTokens = tokens.slice(position + deleteCount);
    return tokens.splice(position, deleteCount + afterTokens.length, ...[...newTokens, ...afterTokens].reduce((acc, { type, lexeme, literal }) => {
        const previousToken = (0, lodash_1.nth)(acc, -1) ?? (position > 0 ? tokens[position - 1] : null);
        if (type === __1.TokenType.EOF) {
            const token = new __1.Token(__1.TokenType.EOF, '', null, line, previousToken?.start ?? firstStart, previousToken?.end ?? firstStart);
            previousToken?.setNext(token);
            return [...acc, token];
        }
        const start = previousToken ? previousToken.end : firstStart;
        const end = start + lexeme.length;
        const token = new __1.Token(type, lexeme, literal ?? null, line, start, end);
        previousToken?.setNext(token);
        return [...acc, token];
    }, []));
};
exports.spliceTokens = spliceTokens;
const unwrap = (str) => {
    if (str.startsWith('"') && str.endsWith('"')) {
        return str.slice(1, -1);
    }
    return str;
};
const getMutationByPropArguments = (tokens, mutations) => {
    if ((0, lodash_1.first)(tokens)?.type === __1.TokenType.Identifier) {
        const key = (0, exports.serializeTokens)(tokens);
        return mutations.find((mutation) => unwrap(mutation.prev) === unwrap(key));
    }
    else if (tokens.length === 1 && (0, lodash_1.first)(tokens)?.type === __1.TokenType.String) {
        return mutations.find((mutation) => unwrap(mutation.prev) === unwrap((0, lodash_1.first)(tokens)?.literal));
    }
    return undefined;
};
/**
 * Convert prop arguments in penguino expressions.
 *
 * Note: When converting fields to literal names wrap the name in brackets.
 *
 * For example:
 * - prop("My Field (on My Table)") can become prop(myTable.myField)
 * - prop("My Table.My Field") can become prop(myTable.myField)
 * - prop(myTable.myField) can become prop("My Field (on My Table)")
 * - prop(myTable.myField) can become prop("My Table.My Field")
 */
const replacePropertiesInExpression = (expression, mutations) => {
    return (0, exports.serializeTokens)((0, exports.replacePropertiesInExpressionTokens)(new __1.Scanner(expression, { safe: true }).scanTokens(), mutations));
};
exports.replacePropertiesInExpression = replacePropertiesInExpression;
const replacePropertiesInExpressionTokens = (tokens, mutations) => {
    return (0, exports.walkTokens)(tokens, (token, childrenWithParens) => {
        if (!(0, exports.isPropToken)(token)) {
            return [token, ...childrenWithParens];
        }
        const mutation = getMutationByPropArguments(childrenWithParens.slice(1, -1), mutations);
        if (mutation === undefined) {
            return [token, ...childrenWithParens];
        }
        let toTokens = (0, parser_1.tokenize)(mutation.next);
        if ((0, lodash_1.last)(toTokens)?.type === __1.TokenType.EOF) {
            toTokens = toTokens.slice(0, -1);
        }
        return [
            token,
            { type: __1.TokenType.LeftParen, lexeme: '(' },
            ...toTokens,
            { type: __1.TokenType.RightParen, lexeme: ')' },
        ];
    });
};
exports.replacePropertiesInExpressionTokens = replacePropertiesInExpressionTokens;
//# sourceMappingURL=mutations.js.map